import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SelectField } from "@/components/ui/select";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { apiService } from "@/services/api";
import {
  Settings as SettingsIcon,
  User,
  Shield,
  Save,
  RefreshCw,
  Download,
  Eye,
  EyeOff,
} from "lucide-react";
import { TwoFactorAuth } from "@/components/auth/TwoFactorAuth";

export function Settings() {
  const { currentTheme } = useTheme();
  const { user }: any = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoadingSettings(true);
      console.log("Loading admin settings...");
      const token = localStorage.getItem("admin-token");
      console.log("Auth token:", token);
      console.log("Token length:", token?.length);
      console.log("Token starts with:", token?.substring(0, 20));

      // First get the current user data to ensure we have the latest information
      try {
        const userResponse = await apiService.getCurrentAdminUser();
        console.log("Current user data:", userResponse);

        if (userResponse.type === 0) {
          const userData = userResponse.data;
          // Update profile settings with current user data
          setProfileSettings(prev => ({
            ...prev,
            fullName: userData.fullName || prev.fullName,
            email: userData.email || prev.email,
            phone: userData.phoneNumber || prev.phone,
          }));
        }
      } catch (userError) {
        console.error("Error fetching current user data:", userError);
        // Continue with settings even if user data fetch fails
      }

      const response = await apiService.getAdminSettings();
      console.log("Settings API response:", response);

      if (response.type === 0) {
        const settings = response.data;
        console.log("Settings data:", settings);

        if (settings.profile) {
          setProfileSettings(prev => ({
            ...prev,
            ...settings.profile,
            // Ensure we use the most current user data
            fullName: settings.profile.fullName || prev.fullName,
            email: settings.profile.email || prev.email,
            phone: settings.profile.phone || prev.phone,
          }));
        }
        if (settings.security) {
          setSecuritySettings(settings.security);
        }
        if (settings.system) {
          setSystemSettings(settings.system);
        }
        toast.success("Settings loaded successfully");
      } else {
        console.error("Settings API error:", response);
        toast.error(response.message || "Failed to load settings");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      console.error("Error details:", error);
      toast.error("Failed to load settings");
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Profile settings
  const [profileSettings, setProfileSettings] = useState({
    fullName: user?.fullName || "Admin User",
    email: user?.email || "admin@snapshift.com",
    phone: "+1 (555) 123-4567",
    bio: "System administrator for SnapShift platform",
    timezone: "UTC-5",
    language: "en",
  });

  // Security settings
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: true,
    sessionTimeout: "30",
    passwordExpiry: "90",
    loginNotifications: true,
    suspiciousActivityAlerts: true,
  });

  // System settings
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    debugMode: false,
    logRetention: "30",
    maxFileUploadSize: "10",
  });

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.updateProfileSettings(profileSettings);
      if (response.type === 0) {
        toast.success("Profile settings updated successfully");
      } else {
        toast.error(response.message || "Failed to update profile settings");
      }
    } catch (error) {
      toast.error("Failed to update profile settings");
      console.error("Error updating profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSecurity = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.updateSecuritySettings(securitySettings);
      if (response.type === 0) {
        toast.success("Security settings updated successfully");
      } else {
        toast.error(response.message || "Failed to update security settings");
      }
    } catch (error) {
      toast.error("Failed to update security settings");
      console.error("Error updating security:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSystem = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.updateSystemSettings(systemSettings);
      if (response.type === 0) {
        toast.success("System settings updated successfully");
      } else {
        toast.error(response.message || "Failed to update system settings");
      }
    } catch (error) {
      toast.error("Failed to update system settings");
      console.error("Error updating system:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Please fill in both password fields");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.changePassword({
        currentPassword,
        newPassword,
      });
      if (response.type === 0) {
        toast.success("Password changed successfully");
        setCurrentPassword("");
        setNewPassword("");
      } else {
        toast.error(response.message || "Failed to change password");
      }
    } catch (error) {
      toast.error("Failed to change password");
      console.error("Error changing password:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportSettings = async () => {
    try {
      const response = await apiService.exportSettings();
      if (response.type === 0) {
        const settings = response.data;
        const blob = new Blob([JSON.stringify(settings, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "snapshift-settings.json";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Settings exported successfully");
      } else {
        toast.error(response.message || "Failed to export settings");
      }
    } catch (error) {
      toast.error("Failed to export settings");
      console.error("Error exporting settings:", error);
    }
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Loading settings...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">Manage your account settings and preferences</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={handleExportSettings}>
            <Download className="w-4 h-4 mr-2" />
            Export Settings
          </Button>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="profile" className="flex items-center space-x-2">
            <User className="w-4 h-4" />
            <span>Profile</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center space-x-2">
            <Shield className="w-4 h-4" />
            <span>Security</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center space-x-2">
            <SettingsIcon className="w-4 h-4" />
            <span>System</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Settings */}
        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal information and preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    value={profileSettings.fullName}
                    onChange={e => {
                      setProfileSettings(prev => ({
                        ...prev,
                        fullName: e.target.value,
                      }));
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileSettings.email}
                    onChange={e => {
                      setProfileSettings(prev => ({ ...prev, email: e.target.value }));
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={profileSettings.phone}
                    onChange={e => {
                      setProfileSettings(prev => ({ ...prev, phone: e.target.value }));
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <SelectField
                    value={profileSettings.timezone}
                    onValueChange={value => {
                      setProfileSettings(prev => ({ ...prev, timezone: value }));
                    }}
                    options={[
                      { value: "UTC-8", label: "UTC-8 (PST)" },
                      { value: "UTC-5", label: "UTC-5 (EST)" },
                      { value: "UTC+0", label: "UTC+0 (GMT)" },
                      { value: "UTC+1", label: "UTC+1 (CET)" },
                      { value: "UTC+5:30", label: "UTC+5:30 (IST)" },
                    ]}
                    placeholder="Select timezone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <SelectField
                    value={profileSettings.language}
                    onValueChange={value => {
                      setProfileSettings(prev => ({ ...prev, language: value }));
                    }}
                    options={[
                      { value: "en", label: "English" },
                      { value: "es", label: "Spanish" },
                      { value: "fr", label: "French" },
                      { value: "de", label: "German" },
                      { value: "zh", label: "Chinese" },
                    ]}
                    placeholder="Select language"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={profileSettings.bio}
                  onChange={e => {
                    setProfileSettings(prev => ({ ...prev, bio: e.target.value }));
                  }}
                  rows={3}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Manage your account security and authentication preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Two-Factor Authentication Component */}
              {user?._id && <TwoFactorAuth userId={user._id} />}
              
              <Separator />
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Other Security Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Login Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Get notified when someone logs into your account
                      </p>
                    </div>
                    <Switch
                      checked={securitySettings.loginNotifications}
                      onCheckedChange={checked => {
                        setSecuritySettings(prev => ({
                          ...prev,
                          loginNotifications: checked,
                        }));
                      }}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Suspicious Activity Alerts</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive alerts for unusual account activity
                      </p>
                    </div>
                    <Switch
                      checked={securitySettings.suspiciousActivityAlerts}
                      onCheckedChange={checked => {
                        setSecuritySettings(prev => ({
                          ...prev,
                          suspiciousActivityAlerts: checked,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Session Management</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
                    <SelectField
                      value={securitySettings.sessionTimeout}
                      onValueChange={value => {
                        setSecuritySettings(prev => ({
                          ...prev,
                          sessionTimeout: value,
                        }));
                      }}
                      options={[
                        { value: "15", label: "15 minutes" },
                        { value: "30", label: "30 minutes" },
                        { value: "60", label: "1 hour" },
                        { value: "120", label: "2 hours" },
                      ]}
                      placeholder="Select session timeout"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="passwordExpiry">Password Expiry (days)</Label>
                    <SelectField
                      value={securitySettings.passwordExpiry}
                      onValueChange={value => {
                        setSecuritySettings(prev => ({
                          ...prev,
                          passwordExpiry: value,
                        }));
                      }}
                      options={[
                        { value: "30", label: "30 days" },
                        { value: "60", label: "60 days" },
                        { value: "90", label: "90 days" },
                        { value: "180", label: "180 days" },
                      ]}
                      placeholder="Select password expiry"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Change Password</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={e => setCurrentPassword(e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => {
                          setShowNewPassword(!showNewPassword);
                        }}
                      >
                        {showNewPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleChangePassword} disabled={isLoading} variant="outline">
                    {isLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveSecurity}
                  disabled={isLoading}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Settings */}
        <TabsContent value="system" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Settings</CardTitle>
              <CardDescription>Configure system-wide settings and preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">System Configuration</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Maintenance Mode</Label>
                      <p className="text-sm text-muted-foreground">
                        Enable maintenance mode to restrict access
                      </p>
                    </div>
                    <Switch
                      checked={systemSettings.maintenanceMode}
                      onCheckedChange={checked => {
                        setSystemSettings(prev => ({
                          ...prev,
                          maintenanceMode: checked,
                        }));
                      }}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Debug Mode</Label>
                      <p className="text-sm text-muted-foreground">
                        Enable debug mode for development
                      </p>
                    </div>
                    <Switch
                      checked={systemSettings.debugMode}
                      onCheckedChange={checked => {
                        setSystemSettings(prev => ({ ...prev, debugMode: checked }));
                      }}
                    />
                  </div>
                  <Separator />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Storage & Logs</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="logRetention">Log Retention (days)</Label>
                    <SelectField
                      value={systemSettings.logRetention}
                      onValueChange={value => {
                        setSystemSettings(prev => ({ ...prev, logRetention: value }));
                      }}
                      options={[
                        { value: "7", label: "7 days" },
                        { value: "30", label: "30 days" },
                        { value: "90", label: "90 days" },
                        { value: "365", label: "1 year" },
                      ]}
                      placeholder="Select log retention"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxFileUploadSize">Max File Upload Size (MB)</Label>
                    <SelectField
                      value={systemSettings.maxFileUploadSize}
                      onValueChange={value => {
                        setSystemSettings(prev => ({
                          ...prev,
                          maxFileUploadSize: value,
                        }));
                      }}
                      options={[
                        { value: "5", label: "5 MB" },
                        { value: "10", label: "10 MB" },
                        { value: "25", label: "25 MB" },
                        { value: "50", label: "50 MB" },
                      ]}
                      placeholder="Select max file size"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveSystem}
                  disabled={isLoading}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
